export type SkillCategory =
  | 'Communications'
  | 'Reporting'
  | 'Ad Operations'
  | 'Finance'
  | 'Creator Success'
  | 'Sales'
  | 'Partnerships'
  | 'Other';

export interface SkillDisplay {
  displayName: string;
  shortDescription: string;
  icon: string;
  category: SkillCategory;
}

export const SKILL_DISPLAY_META: Record<string, SkillDisplay> = {
  'slack-summary': {
    displayName: 'Summarize Slack',
    shortDescription: 'Recap of your Slack mentions, DMs, and channel activity, with a direct link to every message.',
    icon: '💬',
    category: 'Communications',
  },
  'email-digest': {
    displayName: 'Email Inbox Digest',
    shortDescription: 'Newsletter-style summary of recent emails from a delegated mailbox (e.g. creators@).',
    icon: '📥',
    category: 'Communications',
  },
  'network-performance-report': {
    displayName: 'Network Performance Report',
    shortDescription: 'Monthly recap of top channels by month-over-month growth, with biggest gainers and decliners.',
    icon: '📈',
    category: 'Reporting',
  },
  'ad-ops-workflows': {
    displayName: 'Ad Ops & Pacing',
    shortDescription: 'Google Ad Manager pacing checks, Monday Pacing Reports, and ad operations status updates.',
    icon: '🎯',
    category: 'Ad Operations',
  },
  'revenue-analytics': {
    displayName: 'Revenue Analytics',
    shortDescription: 'Financial breakdowns, revenue forecasts, and ROI analyses for campaigns and channels.',
    icon: '💰',
    category: 'Finance',
  },
  'content-strategy': {
    displayName: 'Content Strategy',
    shortDescription: 'YouTube video performance, channel growth, and content optimization recommendations.',
    icon: '🎬',
    category: 'Creator Success',
  },
  'creator-research': {
    displayName: 'Creator Research',
    shortDescription: 'Searches creators by demographics, builds profiles, and assembles outreach lists.',
    icon: '🔎',
    category: 'Creator Success',
  },
  'outreach-intelligence': {
    displayName: 'Sales Outreach',
    shortDescription: 'Drafts and analyzes outbound sales emails using the MOI framework.',
    icon: '📧',
    category: 'Sales',
  },
  'partner-outreach': {
    displayName: 'Partner Outreach',
    shortDescription: 'Drafts and analyzes outbound partner emails using the MOI framework.',
    icon: '🤝',
    category: 'Partnerships',
  },
  'io-reconciliation': {
    displayName: 'IO Reconciliation',
    shortDescription: 'Compare active Insertion Order impression goals with Google Ad Manager delivery status.',
    icon: '📋',
    category: 'Ad Operations',
  },
};

const titleCase = (slug: string): string =>
  slug
    .split('-')
    .map((part) => (part ? part[0].toUpperCase() + part.slice(1) : part))
    .join(' ');

export const getSkillDisplay = (
  apiName: string,
  apiDescription?: string,
): SkillDisplay => {
  const meta = SKILL_DISPLAY_META[apiName];
  if (meta) return meta;
  return {
    displayName: titleCase(apiName),
    shortDescription: apiDescription || '',
    icon: '✨',
    category: 'Other',
  };
};
